<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { Search16 } from '@carbon/icons-vue';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from 'next/polaris/ui/sidebar';
import PolarisMark from './PolarisMark.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import SidebarNotificationBell from './SidebarNotificationBell.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import ContextNavItems from './ContextNavItems.vue';
import ContextTools from './ContextTools.vue';
import ContextToolsPicker from './ContextToolsPicker.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import CreateAssistantDialog from 'dashboard/components-next/polaris/pageComponents/assistant/CreateAssistantDialog.vue';
import CreatePortalDialog from 'dashboard/components-next/HelpCenter/PortalSwitcher/CreatePortalDialog.vue';

// Tier 1. Labels by default; collapsed, a 44px icon rail with tooltips. On a phone it is the navigation sheet,
// with the current area's menu inside (kit, GlobalSidebar.vue).
const props = defineProps({
  areas: { type: Array, required: true },
  activeArea: { type: Object, default: null },
  isItemActive: { type: Function, required: true },
  side: { type: String, default: 'left' },
  isMobileSidebarOpen: { type: Boolean, default: false },
  showChangelog: { type: Boolean, default: false },
});

const emit = defineEmits([
  'selectArea',
  'closeMobileSidebar',
  'openKeyShortcutModal',
  'showCreateAccountModal',
]);

const { isMobile, state, setOpenMobile } = useSidebar();
const isCollapsed = computed(
  () => !isMobile.value && state.value === 'collapsed'
);
const tip = text => (isMobile.value ? undefined : text);
const railPad = 'group-data-[collapsible=icon]:px-1.5';

const mainAreas = computed(() =>
  props.areas.filter(a => a.placement === 'main')
);
const footerAreas = computed(() =>
  props.areas.filter(a => a.placement === 'footer')
);

// The top bar's menu button opens the sheet; closing it (swipe, Escape, a link) tells the dashboard.
watch(
  () => props.isMobileSidebarOpen,
  value => setOpenMobile(value),
  { immediate: true }
);
const closeSheet = () => {
  if (isMobile.value) emit('closeMobileSidebar');
};

// ✎ in the sheet: the sheet is modal (anything outside it takes no pointer and the first touch outside closes it),
// so the compose form lives outside the sheet and opens as the sheet closes.
const sheetCompose = ref(null);
const composeFromSheet = async () => {
  emit('closeMobileSidebar');
  await nextTick();
  sheetCompose.value?.show();
};

// "Create assistant" / "create portal" in the sheet's picker: same problem as ✎ above, a native <dialog> modal
// opening while the sheet's own modal is still open. The dialogs live outside the sheet and open as it closes.
const sheetCreateAssistantDialog = ref(null);
const sheetCreatePortalDialog = ref(null);
const createAssistantFromSheet = async () => {
  emit('closeMobileSidebar');
  await nextTick();
  sheetCreateAssistantDialog.value?.dialogRef.open();
};
const createPortalFromSheet = async () => {
  emit('closeMobileSidebar');
  await nextTick();
  sheetCreatePortalDialog.value?.dialogRef.open();
};
</script>

<template>
  <Sidebar
    collapsible="icon"
    :side="side"
    class="relative h-full"
    @update:open="value => !value && emit('closeMobileSidebar')"
  >
    <!-- relative h-full, not the registry's fixed full-viewport column: the dashboard can have a banner above it,
      and tier 1 has to start below the banner like tier 2 and the page do -->
    <!-- the brand and the account (spec: "marca e conta no topo"), as the old header drew them; in the rail the
         collapsed switcher is the mark itself -->
    <SidebarHeader :class="railPad">
      <template v-if="isCollapsed">
        <SidebarAccountSwitcher
          is-collapsed
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
        <div class="mt-1 flex justify-center">
          <SidebarNotificationBell compact />
        </div>
      </template>
      <div v-else class="flex items-center gap-2 min-w-0 px-2 h-8">
        <PolarisMark />
        <SidebarAccountSwitcher
          class="flex-grow -mx-1 min-w-0"
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
        <SidebarNotificationBell :is-mobile="isMobile" @navigate="closeSheet" />
      </div>
    </SidebarHeader>
    <nav
      :aria-label="$t('SIDEBAR.GLOBAL_NAV')"
      class="flex min-h-0 flex-1 flex-col"
    >
      <SidebarContent>
        <SidebarGroup :class="railPad">
          <SidebarGroupContent>
            <SidebarMenu class="list-none">
              <SidebarMenuItem v-for="area in mainAreas" :key="area.name">
                <SidebarMenuButton
                  as-child
                  :tooltip="tip(area.label)"
                  :is-active="activeArea?.name === area.name"
                >
                  <RouterLink
                    :to="area.to"
                    :data-area="area.name"
                    :aria-current="
                      activeArea?.name === area.name ? 'page' : undefined
                    "
                    @click="emit('selectArea', area.name)"
                  >
                    <component :is="area.icon" aria-hidden="true" />
                    <span>{{ area.label }}</span>
                  </RouterLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <template v-if="isMobile && activeArea">
          <SidebarSeparator class="data-[orientation=horizontal]:w-auto" />
          <SidebarGroup>
            <div class="flex items-center gap-1 pe-1">
              <SidebarGroupLabel class="flex-1 min-w-0">
                {{ activeArea.label }}
              </SidebarGroupLabel>
              <ContextTools
                :area="activeArea"
                in-sheet
                @compose="composeFromSheet"
              />
            </div>
            <ContextToolsPicker
              :area="activeArea"
              in-sheet
              @create-assistant="createAssistantFromSheet"
              @create-portal="createPortalFromSheet"
            />
            <SidebarGroupContent>
              <ContextNavItems
                :items="activeArea.items"
                :is-item-active="isItemActive"
                @navigate="closeSheet"
              />
            </SidebarGroupContent>
          </SidebarGroup>
        </template>
      </SidebarContent>
      <SidebarFooter :class="railPad">
        <SidebarChangelogCard v-if="showChangelog && !isCollapsed" />
        <SidebarMenu class="list-none">
          <SidebarMenuItem>
            <SidebarMenuButton as-child :tooltip="tip($t('SIDEBAR.SEARCH'))">
              <RouterLink :to="{ name: 'search' }" @click="closeSheet">
                <Search16 aria-hidden="true" />
                <span>{{ $t('SIDEBAR.SEARCH') }}</span>
              </RouterLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem v-for="area in footerAreas" :key="area.name">
            <SidebarMenuButton
              as-child
              :tooltip="tip(area.label)"
              :is-active="activeArea?.name === area.name"
            >
              <RouterLink
                :to="area.to"
                :data-area="area.name"
                :aria-current="
                  activeArea?.name === area.name ? 'page' : undefined
                "
                @click="emit('selectArea', area.name)"
              >
                <component :is="area.icon" aria-hidden="true" />
                <span>{{ area.label }}</span>
              </RouterLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarProfileMenu
          :is-collapsed="isCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </SidebarFooter>
    </nav>
    <SidebarRail />
  </Sidebar>
  <ComposeConversation v-if="isMobile" ref="sheetCompose" align="start">
    <template #trigger />
  </ComposeConversation>
  <CreateAssistantDialog
    v-if="isMobile"
    ref="sheetCreateAssistantDialog"
    type="create"
  />
  <CreatePortalDialog v-if="isMobile" ref="sheetCreatePortalDialog" />
</template>
